// React-free: part of the `@atoms/atom-client/i18n` entry. Keep React and hooks out of this file.
export const DEFAULT_CURRENCY = "EUR";

export function formatNumberForLocale(
  locale: string,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

export function formatCurrencyForLocale(
  locale: string,
  value: number,
  currency: string = DEFAULT_CURRENCY,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}

export function formatDateForLocale(
  locale: string,
  value: Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, options).format(value);
}

/**
 * Numeric day/month/year. Replaces hardcoded `dd.MM.yyyy` patterns — that
 * ordering is itself a German convention, so the pattern has to be localised
 * along with the locale, not just the month names.
 */
export const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

export function formatShortDate(locale: string, value: Date): string {
  return formatDateForLocale(locale, value, SHORT_DATE_OPTIONS);
}

/**
 * Separators the active locale uses for grouping and decimals. Needed to parse
 * user input that was rendered with `formatNumberForLocale`.
 */
export function getNumberSeparatorsForLocale(locale: string): {
  group: string;
  decimal: string;
} {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    group: parts.find((part) => part.type === "group")?.value ?? ",",
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
  };
}

const DIGITS = /^\d+$/;
const WHITESPACE = /\s+/g;
// Direction marks Intl puts around signs and numbers in RTL locales.
const BIDI_MARKS = /[\u200e\u200f\u061c]/g;
const APOSTROPHES = ["'", "\u2019"];
const MINUS_SIGNS = ["-", "\u2212"];

/** How a locale writes numbers, as far as parsing needs it. */
interface NumberNotation {
  group: string;
  decimal: string;
  /** The locale's digits, indexed by their value; `"0"`…`"9"` for Latin digits. */
  digits: string[];
  /** The size of the last group before the decimal separator, 3 in most locales. */
  primaryGroupSize: number;
  /** The size of the groups before it, 2 in `en-IN`. */
  secondaryGroupSize: number;
}

const notations = new Map<string, NumberNotation>();

function getNumberNotation(locale: string): NumberNotation {
  const cached = notations.get(locale);
  if (cached) return cached;
  const { group, decimal } = getNumberSeparatorsForLocale(locale);
  const digitFormat = new Intl.NumberFormat(locale, { useGrouping: false });
  const digits = Array.from({ length: 10 }, (_, digit) =>
    digitFormat.format(digit).replaceAll(BIDI_MARKS, ""),
  );
  const groupSizes = new Intl.NumberFormat(locale)
    .formatToParts(1234567890)
    .filter((part) => part.type === "integer")
    // Counted in code points: some numbering systems use surrogate pairs.
    .map((part) => Array.from(part.value).length);
  const primaryGroupSize = groupSizes.at(-1) ?? 3;
  const secondaryGroupSize =
    groupSizes.length > 2 ? (groupSizes.at(-2) ?? 3) : primaryGroupSize;
  const notation = {
    group,
    decimal,
    digits,
    primaryGroupSize,
    secondaryGroupSize,
  };
  notations.set(locale, notation);
  return notation;
}

/**
 * The digits of a grouped integer part, or `undefined` when the groups do not
 * have the locale's sizes ("1,23" in `en-US`).
 */
function ungroupDigits(
  integer: string,
  notation: NumberNotation,
): string | undefined {
  if (!integer.includes(notation.group)) return integer;
  const groups = integer.split(notation.group);
  const last = groups.length - 1;
  const wellFormed = groups.every((part, index) => {
    if (!DIGITS.test(part)) return false;
    if (index === last) return part.length === notation.primaryGroupSize;
    // Default formats never start a grouped number with a zero group: "0,125"
    // is not 125.
    if (index === 0) {
      return (
        !part.startsWith("0") && part.length <= notation.secondaryGroupSize
      );
    }
    return part.length === notation.secondaryGroupSize;
  });
  return wellFormed ? groups.join("") : undefined;
}

/** A number without sign, written with `decimal` and, if given, the locale's groups. */
function parseUnsignedNumber(
  text: string,
  decimal: string,
  notation: NumberNotation | undefined,
): number | undefined {
  const parts = text.split(decimal);
  if (parts.length > 2) return undefined;
  const [integerPart = "", fraction = ""] = parts;
  if (integerPart === "" && fraction === "") return undefined;
  if (fraction !== "" && !DIGITS.test(fraction)) return undefined;
  const integer = notation ? ungroupDigits(integerPart, notation) : integerPart;
  if (integer === undefined || (integer !== "" && !DIGITS.test(integer))) {
    return undefined;
  }
  const parsed = Number(`${integer || "0"}.${fraction || "0"}`);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** `text` without direction marks, with the locale's digits as Latin digits. */
function normalizeNumberText(text: string, notation: NumberNotation): string {
  let normalized = text.replaceAll(BIDI_MARKS, "").trim();
  notation.digits.forEach((digit, value) => {
    if (digit !== `${value}`) {
      normalized = normalized.replaceAll(digit, `${value}`);
    }
  });
  // ICU builds write the apostrophe group of de-CH as ' or as U+2019.
  if (APOSTROPHES.includes(notation.group)) {
    for (const apostrophe of APOSTROPHES) {
      normalized = normalized.replaceAll(apostrophe, notation.group);
    }
  }
  // A locale that groups with a space takes any space as its group separator.
  if (notation.group.trim() === "") {
    normalized = normalized.replaceAll(WHITESPACE, notation.group);
  }
  return normalized;
}

/**
 * Whether `text` is an integer grouped by dots in threes, e.g. "1.234.567".
 * A first group with a leading zero ("0.125", "012.345") is a decimal.
 */
function isDotGroupedInteger(text: string): boolean {
  const [first = "", ...rest] = text.split(".");
  return (
    rest.length > 0 &&
    DIGITS.test(first) &&
    !first.startsWith("0") &&
    first.length <= 3 &&
    rest.every((part) => DIGITS.test(part) && part.length === 3)
  );
}

/**
 * Parses a number the user typed in `locale`, the counterpart of
 * `formatNumberForLocale`: `"1.234,56"` is 1234.56 in `de-DE`, `"1,234.56"`
 * in `en-US`. Group separators are optional, but where they appear the groups
 * must have the locale's sizes; a locale that groups with a space accepts any
 * space. The locale's own digits (Arabic-Indic in `ar-EG`) and the direction
 * marks of RTL locales are understood. A leading `-` (or `−`) makes the
 * number negative.
 *
 * Where the locale's decimal separator is not a dot, a dot is read as the
 * decimal point when the text has no decimal separator of the locale and is
 * not an integer grouped in threes: in `de-DE`, `"12.5"` is 12.5 and
 * `"1.234"` is 1234, `"0.125"` is 0.125. A grouped number does not start with
 * a zero group, so `"0,125"` in `en-US` is invalid, not 125.
 *
 * Returns `undefined` for empty or invalid text: letters, exponents, a second
 * decimal separator, a group separator after the decimal separator.
 */
export function parseNumberForLocale(
  locale: string,
  text: string,
): number | undefined {
  const notation = getNumberNotation(locale);
  const { group, decimal } = notation;
  let body = normalizeNumberText(text, notation);

  const negative = MINUS_SIGNS.some((sign) => body.startsWith(sign));
  if (negative || body.startsWith("+")) body = body.slice(1);

  const dotIsDecimal =
    decimal !== "." &&
    body.includes(".") &&
    !body.includes(decimal) &&
    !(group === "." && isDotGroupedInteger(body));
  // With a dot as the decimal point, the locale's groups still apply,
  // unless the locale groups with a dot.
  const dotDecimalGroups = group === "." ? undefined : notation;
  const magnitude = dotIsDecimal
    ? parseUnsignedNumber(body, ".", dotDecimalGroups)
    : parseUnsignedNumber(body, decimal, notation);
  if (magnitude === undefined) return undefined;
  return negative ? -magnitude : magnitude;
}

/**
 * Human-readable name of `locale`, rendered in `inLocale` (its own language by
 * default, which is what language switchers should show).
 */
export function getLocaleDisplayName(
  locale: string,
  inLocale: string = locale,
): string {
  try {
    const displayNames = new Intl.DisplayNames([inLocale], {
      type: "language",
    });
    const name = displayNames.of(locale);
    if (name && name !== locale) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  } catch {
    // Fall through to the raw tag below.
  }
  return locale;
}

export type LocaleFormat = {
  locale: string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
  formatDate: (value: Date, options?: Intl.DateTimeFormatOptions) => string;
};
